import React, { useMemo, useCallback, useEffect } from 'react';
import './style.scss';
import moment from 'moment-timezone';
import _ from 'lodash';
import StarRatingsChart from 'components/Clinic/ClinicStats/StarRatingsChart';
import { PhysicianImage } from 'components/Common/UserImage';
import PhysicianUtils from 'services/PhysicianUtils';
import SubscriptionsChart from 'components/Clinic/ClinicStats/SubscriptionsChart';
import RevenueChart from 'components/Clinic/ClinicStats/RevenueChart';
import SimpleStackedChart from 'components/Clinic/ClinicStats/SimpleStackedChart';
import ATable from 'components/Clinic/ClinicFinancials/ATable';
import StaffUtils from 'services/StaffUtils';
import { useAppSelector } from 'careand-redux/store';
import CalendarUtils from 'services/CalendarUtils';
import SubscriptionUtils from 'services/SubscriptionUtils';

const DEFAULT_ARRAY = [];

function getLeadTime(appt) {
  const day = moment.unix(appt.date).tz(CalendarUtils.getTargetTimezone());
  const day_booking = moment.unix(appt.date_booking).tz(CalendarUtils.getTargetTimezone());
  const leadTime = (appt.date - appt.date_booking) / (60 * 60 * 24);

  if (day.isSame(day_booking, 'day')) {
    return 0;
  }

  return leadTime;
}

/**
 *  @param {{
 *    physicians: Partial<Record<string, import('generated/api').Physician>>,
 *    appointments: import('generated/api').Appointment[]
 *    clinicId: string | undefined
 *  }} args
 */
export const LeadTimeToAppointmentPhysicianOnly = ({ physicians, appointments, clinicId }) => {
  const FORMAT = 'MM YYYY';

  function getAux(appts) {
    const byMonth = _.groupBy(appts, (it) => moment.unix(it.date_booking).format(FORMAT));
    return _(byMonth)
      .mapValues((appts, key) => {
        return {
          x: moment(key, FORMAT),
          y: appts.length,
        };
      })
      .values()
      .sortBy('x')
      .flatten()
      .value();
  }

  const groups = _(appointments)
    .filter((it) => physicians[it.physician_id])
    .filter((it) => (clinicId ? it.clinic_id === clinicId : true))
    .filter((it) => {
      return !it.status || it.status === 'Completed';
    })
    .filter((it) => getLeadTime(it) >= 0)
    .groupBy((appt) => {
      const lead = Math.ceil(getLeadTime(appt));
      return lead < 7 ? lead + '' : '7+';
    })
    .mapValues((appts, key) => {
      return {
        label: key,
        values: getAux(appts),
      };
    })
    .values()
    .value();

  return (
    <SimpleStackedChart
      datasets={groups}
      title="Lead time to appointment"
      unit="month"
      stacked={true}
      fill={true}
      ticks={{
        callback: (value) => value + ' appts',
      }}
      optionsOverride={{
        plugins: {
          tooltip: {
            itemSort: (a, b) => {
              const al = a.dataset.label;
              const bl = b.dataset.label;
              return al === bl ? 0 : al < bl ? 1 : -1;
            },
          },
        },
      }}
    />
  );
};

/**
 *  @param {{
 *    physicians: Partial<Record<string, import('generated/api').Physician>>,
 *    clinicPhysicians: Partial<Record<string, import('generated/api').Physician>>,
 *    appointments: import('generated/api').Appointment[]
 *    clinicId: string | undefined
 *  }} args
 */
export const LeadTimeToAppointment = ({ physicians, clinicPhysicians, appointments, clinicId }) => {
  const FORMAT = 'MM YYYY';

  function getValuesFromPhysicianAppointments(appts) {
    return _(appts)
      .groupBy((it) => moment.unix(it.date_booking).format(FORMAT))
      .mapValues((appts, key) => {
        const sumLeadTime = appts.reduce((acc, next) => {
          const leadTime = getLeadTime(next);
          if (leadTime > 15) {
            return acc;
          }
          return acc + leadTime;
        }, 0);
        const avgLeadTime = sumLeadTime / appts.length;
        return {
          x: moment(key, FORMAT),
          y: avgLeadTime,
        };
      })
      .values()
      .sortBy('x')
      .flatten()
      .value();
  }

  const groups = useMemo(() => {
    const groups = [];
    if (clinicPhysicians) {
      const appts = _(appointments)
        .filter((it) => clinicPhysicians[it.physician_id])
        .filter((it) => getLeadTime(it) >= 0)
        .filter((it) => {
          return !it.status || it.status === 'Completed';
        })
        .value();

      groups.push({
        label: 'Clinic Average',
        values: getValuesFromPhysicianAppointments(appts),
      });
    }

    _(appointments)
      .filter((it) => physicians[it.physician_id])
      .filter((it) => (clinicId ? it.clinic_id === clinicId : true))
      .filter((it) => getLeadTime(it) >= 0)
      .filter((it) => {
        return !it.status || it.status === 'Completed';
      })
      .groupBy('physician_id')
      .mapValues((appts, key) => {
        return {
          label: PhysicianUtils.getName(physicians[key]),
          values: getValuesFromPhysicianAppointments(appts),
        };
      })
      .values()
      .value()
      .forEach((dataset) => groups.push(dataset));

    return groups;
  }, [appointments, physicians, clinicId, clinicPhysicians]);

  return (
    <SimpleStackedChart
      datasets={groups}
      title="Lead time to appointment"
      unit="month"
      stacked={false}
      fill={false}
      ticks={{
        callback: (value) => value + ' days',
      }}
    />
  );
};

export const BlockedTime = ({ blocked = DEFAULT_ARRAY, physicians }) => {
  const datasets = useMemo(() => {
    const FORMAT = 'W/YYYY';

    return blocked
      .filter((it) => physicians[it.physician_id])
      .map(({ values, physician_id }) => {
        let byWeek = {};

        values.forEach(({ date, value }) => {
          let week = moment.unix(date).format(FORMAT);
          byWeek[week] = byWeek[week] || 0;
          byWeek[week] += value;
        });

        return {
          label: StaffUtils.getName(physicians[physician_id]),
          values: Object.keys(byWeek).map((dateStr) => {
            return {
              x: moment(dateStr, FORMAT),
              y: byWeek[dateStr],
            };
          }),
        };
      });
  }, [blocked, physicians]);

  return (
    <SimpleStackedChart
      datasets={datasets}
      showTitle={false}
      unit="week"
      stacked={false}
      fill={false}
      ticks={{
        callback: (value) => value + 'h',
      }}
    />
  );
};

const DEFAULT_APPOINTMENT_TYPES = ['Home', 'Lab', 'Video', 'Clinic'];

/**@param {any} props*/
export const Appointments = ({
  appointments = DEFAULT_ARRAY,
  title,
  types = DEFAULT_APPOINTMENT_TYPES,
  format = 'MM/YYYY',
  increment = false,
  average = false,
  dateKey = 'date',
  chartOptions = {},
}) => {
  const datasets = useMemo(() => {
    const byDate = {};
    const lengths = {};

    appointments.forEach((appointment) => {
      let { info, value } = appointment;
      let date = appointment[dateKey];
      let str = moment.unix(date).format(format);
      byDate[str] = byDate[str] || {};
      byDate[str][info] = byDate[str][info] || 0;
      lengths[str] = lengths[str] || {};
      lengths[str][info] = lengths[str][info] || 0;

      lengths[str][info] += 1;

      if (increment) {
        byDate[str][info] += 1;
      } else {
        byDate[str][info] += value;
      }
    });

    if (average) {
      Object.keys(byDate).forEach((key) => {
        Object.keys(byDate[key]).forEach((type) => {
          byDate[key][type] = byDate[key][type] / lengths[key][type];
        });
      });
    }

    return Object.values(types).map((type) => {
      return {
        label: type,
        values: Object.keys(byDate).map((key) => {
          return {
            x: moment(key, format),
            y: byDate[key][type],
          };
        }),
      };
    });
  }, [appointments, types, format, increment, dateKey, average]);

  return <SimpleStackedChart datasets={datasets} title={title} {...chartOptions} />;
};

/**
 * @param {{
 *  data: import('generated/api').ClinicStatisticsNew,
 *  physicians: Partial<Record<string, import('generated/api').Physician>>
 *  hideInactive?: boolean
 *  showTitle?: boolean
 * }} Props
 */
export const Revenue = ({ data, physicians, hideInactive, showTitle }) => {
  const datasets = useMemo(() => {
    const rosterById = _(data.rosters)
      .groupBy('physician_id')
      .mapValues((it) => it[0].patients)
      .value();

    const subsById = _(data.subscriptions).groupBy('patient_id').mapValues(_.first).value();

    const subscriptionRevenue = _(physicians)
      .values()
      .map((physician) => {
        const roster = rosterById[physician.id];
        const subs = _(roster)
          .map((pid) => subsById[pid])
          .compact()
          .filter((sub) => SubscriptionUtils.activeSubscriptionUntilThisMonth(sub, moment().unix()))
          .value();

        const revenue = subs.reduce((acc, sub) => {
          const value = (sub.plan?.amount ?? 0) / 100;
          const info = sub.plan.interval;
          return acc + (info === 'year' ? value : value * 12);
        }, 0);

        return {
          x: PhysicianUtils.getName(physician),
          y: revenue,
        };
      })
      .filter((it) => it && it.y > 0)
      .value();

    const ppuRevenue = _(data.appointments)
      .filter((it) => physicians[it.physician_id])
      .filter((it) => it.fee && it.fee > 0)
      .filter((it) => moment.unix(it.date).isSame(moment(), 'year'))
      .groupBy('physician_id')
      .mapValues((appts, pid) => {
        const revenue = appts.reduce((acc, appt) => acc + appt.fee, 0);
        return {
          x: PhysicianUtils.getName(physicians[pid]),
          y: revenue,
        };
      })
      .values()
      .filter((it) => it && it.y > 0)
      .value();

    return [
      {
        label: 'Active Subscriptions',
        values: subscriptionRevenue,
      },
      {
        label: `${moment().format('YYYY')} Pay-Per-Use`,
        values: ppuRevenue,
      },
    ];
  }, [data, physicians, hideInactive]);

  return <RevenueChart datasets={datasets} showTitle={showTitle} />;
};

export const TotalRevenue = ({ data }) => {
  const totalRevenue = useMemo(() => {
    const formatter = new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    });

    const yearString = moment().format('YYYY');

    let subscriptionRevenue = (data?.subscriptionRevenue || [])
      .map(({ values }) => {
        return values
          .filter(({ value }) => value > 0)
          .reduce((acc, { value, info }) => acc + (info === 'year' ? value : value * 12), 0);
      })
      .reduce((acc, value) => acc + value, 0);

    let ppuRevenue = (data?.appointmentRevenue || [])
      .map(({ values }) => {
        return values
          .filter(({ date }) => moment.unix(date).format('YYYY') === yearString)
          .reduce((acc, { value }) => acc + value, 0);
      })
      .reduce((acc, value) => acc + value, 0);
    return formatter.format(subscriptionRevenue + ppuRevenue);
  }, [data]);

  const totalSubscribers = useMemo(() => {
    return (data?.subscriptionRevenue || [])
      .map(({ values }) => {
        return values.filter(({ value }) => value > 0).length;
      })
      .reduce((acc, value) => acc + value, 0);
  }, [data]);

  return (
    <div className="stats">
      <ATable
        headers={['', 'Amount']}
        rows={[
          ['Annual Revenue (excluding HST)', totalRevenue],
          ['Rostered Subscribers', totalSubscribers],
        ]}
      />
    </div>
  );
};

/**
 * @param {{
 * data: import('generated/api').ClinicStatisticsNew,
 * physicians: Record<string, import('generated/api').Physician>,
 * allPhysicians: Record<string, import('generated/api').Physician>,
 * showClinics: boolean,
 * showTitle: boolean,
 * showLegend: boolean,
 * showAverage: boolean,
 * clinicChanges: import('generated/api').StatisticsEvent[]
 * }} args
 */
/**
 * @param {{
 * data: import('generated/api').ClinicStatisticsNew,
 * physicians: Record<string, import('generated/api').Physician | undefined>,
 * allPhysicians: Record<string, import('generated/api').Physician | undefined>,
 * showClinics?: string[],
 * showTitle: boolean,
 * showLegend: boolean,
 * showAverage?: boolean,
 * clinicChanges?: import('generated/api').StatisticsEvent[]
 * }} args
 */
export const Subscriptions = ({
  data,
  physicians,
  allPhysicians,
  showClinics,
  showTitle,
  showLegend,
  showAverage,
}) => {
  const clinics = useAppSelector((state) => state.resource.clinics);

  const clinicChanges = useMemo(
    () => new ClinicChanges(data?.clinicChanges ?? []),
    [data?.clinicChanges],
  );

  // Add diagnostic logging to help track the issue
  useEffect(() => {
    if (showClinics && showClinics.length > 0) {
      showClinics.forEach((clinicId) => {
        const clinic = clinics.find((c) => c.id === clinicId);

        // See which physicians were at this clinic historically
        const physicianHistoryAtClinic = {};

        Object.keys(allPhysicians || {}).forEach((physicianId) => {
          const events = clinicChanges.getPhysicianClinicEvents(physicianId);

          if (events) {
            const atThisClinic = events.some((e) => e.payload.new_id === clinicId);
            if (atThisClinic) {
              physicianHistoryAtClinic[physicianId] = true;
            }
          } else if (allPhysicians[physicianId]?.clinic_id === clinicId) {
            physicianHistoryAtClinic[physicianId] = true;
          }
        });
      });
    }
  }, [showClinics, clinics, allPhysicians, clinicChanges]);

  const calculateClinicDataset = useCallback(
    (clinicId) => {
      const clinic = clinics.find((it) => it.id === clinicId);

      if (!allPhysicians) {
        return;
      }

      const FORMAT = 'MMM YYYY';

      // Every physician's value in a weekly snapshot shares the same event
      // date, so each snapshot is a consistent census. Summing each
      // physician's latest value across DIFFERENT snapshots double-counts
      // patients transferred between physicians mid-month (a departing
      // physician keeps their pre-transfer count while the recipients'
      // post-transfer counts include the same patients). Total each month
      // from the single latest snapshot in that month instead.
      const monthlySnapshots = {};

      _(data?.subscriptionStats || []).forEach(({ values, physician_id }) => {
        values.forEach(({ date, value }) => {
          // Check if this data point belongs to this clinic
          const physicianClinic =
            clinicChanges.getPhysicianClinicAt(physician_id, date) ||
            allPhysicians[physician_id]?.clinic_id;

          if (physicianClinic !== clinicId) {
            return;
          }

          const monthKey = moment.unix(date).format(FORMAT);
          const month = (monthlySnapshots[monthKey] = monthlySnapshots[monthKey] || {});
          const snapshot = (month[date] = month[date] || { physicianCount: 0, total: 0 });
          snapshot.physicianCount += 1;
          snapshot.total += value;
        });
      });

      const clinicAvgDataset = {
        label: (clinic?.name ?? 'Clinic') + (showAverage ? ' Average' : ''),
        values: _(monthlySnapshots)
          .entries()
          .sortBy(([key]) => moment(key, FORMAT).unix())
          .map(([key, snapshots]) => {
            const latestDate = _.max(Object.keys(snapshots).map(Number));
            const { total, physicianCount } = snapshots[latestDate];
            let y = total;
            if (showAverage) {
              y = physicianCount === 0 ? 0 : total / physicianCount;
            }
            return {
              x: moment(key, FORMAT),
              y,
            };
          })
          .value(),
      };

      return clinicAvgDataset;
    },
    [clinics, allPhysicians, data, clinicChanges, showAverage],
  );

  const calculatePhysiciansDataset = useCallback(
    (physicians) => {
      return (data?.subscriptionStats || [])
        .filter((it) => physicians?.[it?.physician_id])
        .map(({ physician_id, values }) => {
          return {
            label: PhysicianUtils.getName(physicians[physician_id]),
            values: values.map(({ value, date }) => {
              return {
                x: moment.unix(date),
                y: value,
              };
            }),
          };
        })
        .filter(Boolean);
    },
    [data],
  );

  const subscriptionsData = useMemo(() => {
    return calculatePhysiciansDataset(physicians);
  }, [calculatePhysiciansDataset, physicians]);

  const clinicAvgDataset = useMemo(() => {
    return (showClinics ?? []).map((id) => calculateClinicDataset(id));
  }, [data, calculateClinicDataset, showClinics]);

  const datasets = useMemo(() => {
    if (clinicAvgDataset) {
      return [...subscriptionsData, ...clinicAvgDataset];
    }
    return subscriptionsData;
  }, [subscriptionsData, clinicAvgDataset]);

  return (
    <SubscriptionsChart
      subscriptionsData={datasets}
      showTitle={showTitle}
      showLegend={showLegend}
    />
  );
};

class ClinicChanges {
  /**
   * @param {import('generated/api').StatisticsEvent[]} changes
   */
  constructor(changes) {
    this.changes = changes.map((it) => ({
      ...it,
      payload: JSON.parse(it.payload),
    }));
    this.byPhysician = _.groupBy(this.changes, (it) => it.user_id);
  }

  /**
   * Get all clinic change events for a physician
   * @param {string} physicianId
   */
  getPhysicianClinicEvents(physicianId) {
    return this.byPhysician[physicianId];
  }

  /**
   * @param {string} physicianId
   * @param {number} date
   */
  getPhysicianClinicAt(physicianId, date) {
    const events = this.byPhysician[physicianId];
    if (!events || events.length === 0) {
      return undefined;
    }

    // Handle case where date is before any recorded events
    if (date < events[0].date) {
      return undefined; // Or a default clinic ID if appropriate
    }

    // Find the latest event that occurred on or before the given date
    const eventOnOrBefore = events
      .filter((event) => event.date <= date)
      .sort((a, b) => b.date - a.date)[0]; // Sort descending and take first

    return eventOnOrBefore ? eventOnOrBefore.payload.new_id : undefined;
  }
}

export const StarRatings = ({ data, physicians, hideInactive }) => {
  const physicianStarRatings = useMemo(() => {
    const thisMonth = moment().startOf('month');
    const thisYear = moment().startOf('year');

    const ratings = _(data?.appointments || [])
      .filter((it) => physicians[it.physician_id] && !!it.rating)
      .groupBy('physician_id')
      .mapValues((values, key) => ({
        values: values.map((it) => ({ value: it.rating, date: it.date })),
        physician_id: key,
      }))
      .values()
      .map(({ values, physician_id }) => {
        let previousData = [0, 0, 0, 0, 0];
        let thisMonthData = [0, 0, 0, 0, 0];
        let thisYearData = [0, 0, 0, 0, 0];

        let total = 0;

        values.forEach(({ date, value }) => {
          if (moment.unix(date).isSameOrAfter(thisMonth)) thisMonthData[value - 1]++;
          else if (moment.unix(date).isSameOrAfter(thisYear)) {
            total++;
            thisYearData[value - 1]++;
          } else previousData[value - 1]++;
        });

        return {
          inactive: physicians[physician_id].inactive,
          hide: physicians[physician_id].hide_portal,
          total,
          physician_id,
          previous: previousData,
          thisMonth: thisMonthData,
          thisYear: thisYearData,
        };
      })
      .filter(({ hide, inactive }) => {
        if (hide) return false;
        if (hideInactive) return !inactive;
        return true;
      })
      .sortBy(['inactive', 'total'])
      .reverse()
      .value();
    return ratings;
  }, [data, physicians, hideInactive]);

  return (
    <>
      {physicianStarRatings.map(({ physician_id, previous, thisMonth, thisYear, inactive }) => {
        let className = 'star-chart-wrapper';
        if (inactive) className += ' inactive';

        return (
          <div className={className} key={physician_id}>
            <StarRatingsChart
              starCounts={{
                'Previous Year': previous,
                'This Year': thisYear,
                'This Month': thisMonth,
              }}
              skipLabels={['Previous Year']}
              headerData={
                <div className="physician-data">
                  <PhysicianImage user={physicians[physician_id]} size={40} />
                  <span>{PhysicianUtils.getName(physicians[physician_id])}</span>
                </div>
              }
            />
          </div>
        );
      })}
    </>
  );
};

/*
 * Reduces the values of StatisticsValues to only use the last one each month
 */
function shrinkValues(values) {
  if (!values || values.length === 0) {
    return [];
  }
  const FORMAT = 'YYYY MM';
  const newValues = [];
  let prevMonth = null;
  let prevValue = null;
  for (let value of values) {
    if (value) {
      const month = moment.unix(value.date).format(FORMAT);
      if (prevMonth === null) {
        prevMonth = month;
        prevValue = value;
        continue;
      }
      if (month !== prevMonth) {
        newValues.push(prevValue);
        prevMonth = month;
      }
      prevValue = value;
    }
  }
  if (
    newValues.length > 0 &&
    prevMonth !== moment.unix(newValues[newValues.length - 1].date).format(FORMAT)
  ) {
    newValues.push(prevValue);
  }
  return newValues;
}
